import React, { useState, useEffect } from 'react';
import { ventasAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { Search, Calendar, Eye, X, Trash2, AlertTriangle, Printer } from 'lucide-react';
import TicketPrinter from '../components/TicketPrinter';

const HistorialVentasPage = () => {
  const { user } = useAuth();
  const { config } = useConfig();
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroFecha, setFiltroFecha] = useState(new Date().toISOString().split('T')[0]);
  const [showConfirmarAnular, setShowConfirmarAnular] = useState(false);
  const [ventaAnular, setVentaAnular] = useState(null);
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const [showDetalle, setShowDetalle] = useState(false);

  useEffect(() => {
    cargarVentas();
  }, [filtroFecha]);

  const cargarVentas = async () => {
    try {
      setLoading(true);
      const response = await ventasAPI.getByFecha(filtroFecha);
      if (response.data.success) {
        setVentas(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar ventas:', error);
      setVentas([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmarAnularVenta = (venta) => {
    setVentaAnular(venta);
    setShowConfirmarAnular(true);
  };

  const anularVenta = async () => {
    if (!ventaAnular) return;
    
    try {
      const response = await ventasAPI.cancel(ventaAnular.id);
      if (response.data.success) {
        alert('Venta anulada exitosamente');
        setShowConfirmarAnular(false);
        setVentaAnular(null);
        cargarVentas();
      }
    } catch (error) {
      alert('Error al anular venta: ' + (error.response?.data?.message || error.message));
    }
  };

  const verDetalle = async (venta) => {
    try {
      const response = await ventasAPI.getById(venta.id);
      if (response.data.success) {
        setVentaDetalle(response.data.data);
        setShowDetalle(true);
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      alert('Error al cargar detalle de la venta');
    }
  };

  const reimprimirTicket = (venta) => {
    const printer = new TicketPrinter({
      nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
      direccion: config?.direccion || '',
      telefono: config?.telefono || '',
      rfc: config?.rfc || ''
    });
    printer.print(venta);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
    }).format(amount || 0);
  };

  const isAdmin = user?.rol === 'admin' || user?.rol === 'gerente';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Historial de Ventas</h1>
      
      {/* Filtros */}
      <div className="mb-6 flex gap-4">
        <input
          type="date"
          value={filtroFecha}
          onChange={(e) => setFiltroFecha(e.target.value)}
          className="input-field"
        />
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Folio</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center">Cargando...</td>
              </tr>
            ) : (
              ventas.map((venta) => (
                <tr key={venta.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">{venta.folio}</td>
                  <td className="px-4 py-3">{venta.cliente_nombre || 'General'}</td>
                  <td className="px-4 py-3">{formatCurrency(venta.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      venta.estado === 'cancelada' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {venta.estado || 'completada'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => verDetalle(venta)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => reimprimirTicket(venta)}
                        className="text-green-600 hover:text-green-800"
                        title="Reimprimir ticket"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      {isAdmin && venta.estado !== 'cancelada' && (
                        <button
                          onClick={() => confirmarAnularVenta(venta)}
                          className="text-red-600 hover:text-red-800"
                          title="Anular venta"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de detalle de venta */}
      {showDetalle && ventaDetalle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Detalle de Venta - {ventaDetalle.folio}</h2>
              <button
                onClick={() => setShowDetalle(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-sm text-gray-600">Cliente:</p>
                  <p className="font-medium">{ventaDetalle.cliente_nombre || 'General'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fecha:</p>
                  <p className="font-medium">{new Date(ventaDetalle.fecha_venta).toLocaleString('es-GT')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vendedor:</p>
                  <p className="font-medium">{ventaDetalle.vendedor_nombre || ventaDetalle.usuario_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Estado:</p>
                  <p className="font-medium">{ventaDetalle.estado || 'Completada'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold mb-2">Productos vendidos:</h3>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs">Producto</th>
                      <th className="px-3 py-2 text-right text-xs">Cant</th>
                      <th className="px-3 py-2 text-right text-xs">Precio</th>
                      <th className="px-3 py-2 text-right text-xs">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventaDetalle.detalle?.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-3 py-2 text-sm">{item.producto_nombre}</td>
                        <td className="px-3 py-2 text-sm text-right">{item.cantidad}</td>
                        <td className="px-3 py-2 text-sm text-right">{formatCurrency(item.precio_unitario)}</td>
                        <td className="px-3 py-2 text-sm text-right">{formatCurrency(item.cantidad * item.precio_unitario)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(ventaDetalle.subtotal)}</span>
                </div>
                {ventaDetalle.descuento > 0 && (
                  <div className="flex justify-between mb-2 text-red-600">
                    <span>Descuento:</span>
                    <span>-{formatCurrency(ventaDetalle.descuento)}</span>
                  </div>
                )}
                {ventaDetalle.impuestos > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>IVA:</span>
                    <span>{formatCurrency(ventaDetalle.impuestos)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-primary-600">{formatCurrency(ventaDetalle.total)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  reimprimirTicket(ventaDetalle);
                  setShowDetalle(false);
                }}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Reimprimir Ticket
              </button>
              <button
                onClick={() => setShowDetalle(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar anular */}
      {showConfirmarAnular && ventaAnular && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-bold">Confirmar Anulación</h2>
            </div>
            
            <p className="mb-4">
              ¿Está seguro de anular la venta {ventaAnular.folio}?
              Esta acción devolverá el stock de los productos.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmarAnular(false)}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={anularVenta}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Anular Venta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorialVentasPage;
